// Client-side .docx rendering — the local replacement for the original add-in's
// server-side Syncfusion "RenderAsImages" path. We render the OOXML to HTML pages with
// docx-preview, capture a real text layer from the rendered DOM (word-level boxes, so
// snips get exact selectable text), then rasterise each page with html2canvas for display
// and OCR fallback. Both libraries are dynamically imported so they only load when a user
// actually imports a .docx.
//
// Fidelity note: this renders with browser fonts/CSS, not Word's engine, so complex layouts
// can drift. Good enough for typical business documents; a LibreOffice backend would be exact.

export interface TextBox {
  left: number
  top: number
  right: number
  bottom: number
  str: string
}

export interface DocxPage {
  canvas: HTMLCanvasElement // rasterised page (rendered at RASTER_SCALE for crispness/OCR)
  boxes: TextBox[] // word boxes in page CSS-pixel space (unrotated, scale 1)
  width: number // page CSS width  (matches box coordinate space)
  height: number // page CSS height
}

const RASTER_SCALE = 2 // html2canvas oversampling — sharper display + better OCR

/** Walk the rendered page DOM and emit one box per word, in page-relative CSS pixels. */
function collectTextBoxes(pageEl: HTMLElement, pageRect: DOMRect): TextBox[] {
  const boxes: TextBox[] = []
  const walker = document.createTreeWalker(pageEl, NodeFilter.SHOW_TEXT)
  let node: Node | null
  while ((node = walker.nextNode())) {
    const txt = node.nodeValue ?? ''
    if (!txt.trim()) continue
    const re = /\S+/g
    let m: RegExpExecArray | null
    while ((m = re.exec(txt))) {
      const range = document.createRange()
      range.setStart(node, m.index)
      range.setEnd(node, m.index + m[0].length)
      const r = range.getBoundingClientRect()
      if (r.width === 0 && r.height === 0) continue
      boxes.push({
        left: r.left - pageRect.left,
        top: r.top - pageRect.top,
        right: r.right - pageRect.left,
        bottom: r.bottom - pageRect.top,
        str: m[0],
      })
    }
  }
  return boxes
}

/** Render a .docx (as bytes) into an array of page bitmaps + text layers. */
export async function renderDocx(buf: ArrayBuffer): Promise<DocxPage[]> {
  const [{ renderAsync }, html2canvasMod] = await Promise.all([
    import('docx-preview'),
    import('html2canvas'),
  ])
  const html2canvas = html2canvasMod.default

  const container = document.createElement('div')
  container.style.position = 'fixed'
  container.style.left = '-99999px'
  container.style.top = '0'
  container.style.background = '#ffffff'
  document.body.appendChild(container)

  try {
    await renderAsync(new Uint8Array(buf), container, undefined, {
      className: 'docx',
      inWrapper: true,
      breakPages: true,
      ignoreLastRenderedPageBreak: false,
      experimental: true,
    })

    const pageEls = Array.from(container.querySelectorAll('section.docx')) as HTMLElement[]
    const els = pageEls.length ? pageEls : [container]

    const pages: DocxPage[] = []
    for (const el of els) {
      const rect = el.getBoundingClientRect()
      const boxes = collectTextBoxes(el, rect)
      const canvas = await html2canvas(el, {
        scale: RASTER_SCALE,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      })
      pages.push({ canvas, boxes, width: rect.width, height: rect.height })
    }
    return pages
  } finally {
    document.body.removeChild(container)
  }
}
